// MS-L4B — Public legal page: Termini di utilizzo v1.0 (Pre-Beta / Beta
// gratuita). Content transcribed VERBATIM from the PM-approved DOCX
// (MASTRO_Termini_di_Utilizzo_v1.0_Pre-Beta_Beta-gratuita). Internal project
// markers (top header "DOCUMENTO UFFICIALE DI PROGETTO" and closing "Nota
// di progetto") are intentionally NOT rendered here, in coherence with the
// same principle explicitly declared for the Privacy source document ("La
// sezione «Stato documento» non fa parte del testo pubblico").
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { MastroBrand } from "@/components/MastroBrand";
import { LandingSection } from "@/components/landing/LandingSection";

const P = ({ children }) => (
  <p className="mt-3 text-[15px] leading-relaxed text-neutral-700">{children}</p>
);

const H2 = ({ n, children }) => (
  <h2 className="mt-10 text-lg font-bold tracking-tight text-neutral-950 sm:text-xl">
    {n}. {children}
  </h2>
);

const UL = ({ children }) => (
  <ul className="mt-3 list-disc space-y-1 pl-5 text-[15px] leading-relaxed text-neutral-700">
    {children}
  </ul>
);

export default function Termini() {
  return (
    <div
      data-testid="termini-page"
      className="min-h-screen w-full bg-white text-neutral-950 flex flex-col"
    >
      <LandingSection
        as="header"
        className="border-b border-neutral-100"
        innerClassName="flex items-center justify-between py-4"
      >
        <Link to="/" aria-label="Mastro — Home">
          <MastroBrand className="h-10" />
        </Link>
        <Link
          to="/"
          data-testid="termini-back-home"
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-neutral-600 transition-colors hover:text-neutral-950"
        >
          <ArrowLeft className="h-4 w-4" />
          Torna alla home
        </Link>
      </LandingSection>

      <main className="flex-1">
        <LandingSection innerClassName="py-12 sm:py-16">
          <article className="mx-auto w-full max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-teal-700">
              Mastro
            </p>
            <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-neutral-950 sm:text-4xl">
              Termini di utilizzo di Mastro
            </h1>
            <p
              data-testid="termini-version"
              className="mt-3 text-sm text-neutral-500"
            >
              Versione 1.0 — Pre-Beta / Beta gratuita · Ultimo aggiornamento: 4
              settembre 2026
            </p>

            <P>
              Mastro è attualmente offerto in fase Pre-Beta / Beta gratuita. I
              presenti Termini disciplinano l’accesso e l’utilizzo del servizio
              durante questa fase.
            </P>

            <H2 n={1}>Chi gestisce Mastro</H2>
            <P>
              Mastro è un servizio digitale gestito da Riccardo Gerri. Per
              comunicazioni relative al servizio e alla protezione dei dati
              personali è possibile scrivere a{" "}
              <a
                href="mailto:privacy@iltuomastroapp.it"
                className="text-teal-700 underline-offset-4 hover:underline"
              >
                privacy@iltuomastroapp.it
              </a>
              .
            </P>
            <P>Mastro è attualmente offerto in fase Pre-Beta / Beta gratuita.</P>

            <H2 n={2}>A cosa serve Mastro</H2>
            <P>
              Mastro è un assistente digitale pensato per aiutare artigiani e
              professionisti a organizzare la memoria operativa del proprio
              lavoro. Attraverso WhatsApp e la relativa area web, l’utente può
              affidare a Mastro informazioni relative alla propria attività,
              come lavori, clienti, fotografie, note, misure, aggiornamenti,
              extra, variazioni e informazioni utili alla preparazione di
              preventivi.
            </P>
            <P>
              Mastro organizza queste informazioni e consente all’utente di
              ritrovarle e consultarle successivamente.
            </P>
            <P>
              Mastro è uno strumento di supporto al lavoro dell’utente e non
              sostituisce il controllo professionale dell’artigiano sulle
              informazioni, sulle attività svolte o sui documenti prodotti.
            </P>

            <H2 n={3}>A chi è destinato il servizio</H2>
            <P>
              Mastro è destinato principalmente ad artigiani, lavoratori
              autonomi e professionisti che lo utilizzano nell’ambito della
              propria attività lavorativa. Per creare e utilizzare un account
              è necessario avere almeno 18 anni e avere la capacità di
              accettare i presenti Termini.
            </P>
            <P>
              L’utente deve utilizzare Mastro esclusivamente per finalità
              lecite.
            </P>

            <H2 n={4}>Pre-Beta e Beta gratuita</H2>
            <P>
              Durante la Pre-Beta e la Beta, Mastro è gratuito. Non sono
              previsti:
            </P>
            <UL>
              <li>abbonamenti;</li>
              <li>rinnovi a pagamento;</li>
              <li>addebiti;</li>
              <li>sistemi di pagamento;</li>
              <li>fatturazione del servizio.</li>
            </UL>
            <P>
              L’eventuale introduzione futura di piani commerciali o
              funzionalità a pagamento costituirà una fase separata e sarà
              comunicata preventivamente agli utenti. L’utilizzo della Beta
              non comporta alcun obbligo futuro di acquistare un abbonamento.
            </P>

            <H2 n={5}>Account e accesso</H2>
            <P>
              L’account Mastro è personale e associato all’identità utilizzata
              per accedere al servizio, compreso il numero WhatsApp quando
              previsto dal flusso di registrazione. L’utente è responsabile
              della correttezza delle informazioni fornite e della custodia
              delle proprie credenziali. Le credenziali non devono essere
              condivise con persone non autorizzate. In caso di sospetto
              accesso non autorizzato è opportuno contattare Mastro
              tempestivamente.
            </P>

            <H2 n={6}>Utilizzo di WhatsApp</H2>
            <P>
              Una parte essenziale dell’esperienza di Mastro avviene tramite
              WhatsApp. L’utilizzo di WhatsApp implica anche l’utilizzo dei
              servizi forniti da Meta/WhatsApp, soggetti ai relativi termini e
              alle relative informative. Mastro non controlla il funzionamento
              dell’infrastruttura WhatsApp e non può garantire la
              disponibilità continua di servizi forniti da terze parti.
            </P>

            <H2 n={7}>Intelligenza artificiale</H2>
            <P>
              Mastro utilizza sistemi di intelligenza artificiale per
              interpretare i messaggi e comprendere le richieste dell’utente.
              L’utente viene informato che sta interagendo con un sistema che
              utilizza intelligenza artificiale. Il testo inviato e il
              contesto necessario a comprenderlo possono essere elaborati
              attraverso servizi di intelligenza artificiale. I messaggi
              vocali possono essere sottoposti a trascrizione automatica.
              Nell’architettura attuale di Mastro, le fotografie conservate
              nei lavori non vengono inviate al modello di intelligenza
              artificiale utilizzato per interpretare i messaggi testuali.
            </P>
            <P>
              Mastro utilizza l’intelligenza artificiale come strumento di
              interpretazione e supporto. L’utente deve verificare le
              informazioni rilevanti prima di utilizzarle professionalmente.
              Per determinate operazioni sensibili Mastro può richiedere una
              conferma esplicita dell’utente prima di procedere.
            </P>

            <H2 n={8}>Preventivi e informazioni economiche</H2>
            <P>
              Mastro può aiutare l’utente a organizzare informazioni che
              possono successivamente essere utilizzate per creare un
              preventivo. Il preventivo rimane un documento predisposto sulla
              base delle informazioni fornite o confermate dall’utente. Prima
              di consegnare, inviare o utilizzare professionalmente un
              preventivo, l’utente è responsabile di verificarne contenuti,
              quantità, prezzi, aliquote IVA, dati identificativi e qualsiasi
              altra informazione rilevante.
            </P>
            <P>
              Mastro non sostituisce il commercialista, il consulente fiscale o
              altri professionisti eventualmente necessari.
            </P>

            <H2 n={9}>Contenuti dell’utente</H2>
            <P>
              Le informazioni, fotografie, testi e altri contenuti caricati
              dall’utente rimangono dell’utente o dei rispettivi aventi
              diritto. L’utente concede a Mastro esclusivamente le
              autorizzazioni tecnicamente necessarie per memorizzare,
              elaborare, organizzare e rendere disponibili tali contenuti allo
              scopo di erogare il servizio.
            </P>
            <P>
              Mastro non acquisisce la proprietà dei contenuti dell’utente.
              L’utente è responsabile di avere diritto a utilizzare i
              contenuti che inserisce nel servizio.
            </P>

            <H2 n={10}>Informazioni relative ai clienti dell’artigiano</H2>
            <P>
              Mastro può essere utilizzato per memorizzare informazioni
              relative ai clienti dell’utente o ad altre persone coinvolte
              nei lavori. L’utente decide quali informazioni inserire, per
              quali lavori utilizzarle e per quali finalità professionali
              conservarle.
            </P>
            <P>
              Per tali informazioni, nella misura in cui Mastro le tratta
              esclusivamente per fornire il servizio su istruzione
              dell’utente, l’utente opera come titolare del trattamento e
              Mastro opera per suo conto come responsabile del trattamento.
              Questa distinzione non riguarda i dati che Mastro tratta
              autonomamente per gestire il proprio servizio, gli account, la
              sicurezza, l’assistenza e gli altri trattamenti descritti nella
              Privacy Policy, per i quali il gestore di Mastro opera invece
              come titolare del trattamento.
            </P>

            <H2 n={11}>Trattamento per conto dell’utente — Articolo 28 GDPR</H2>
            <P>
              Per i dati personali relativi ai clienti o ad altri soggetti che
              Mastro tratta per conto dell’utente, la presente sezione
              costituisce parte integrante del rapporto tra l’utente e Mastro.
            </P>
            <UL>
              <li>
                <span className="font-semibold text-neutral-900">
                  Oggetto e durata.
                </span>{" "}
                Mastro tratta tali dati per tutta la durata dell’utilizzo del
                servizio e per il tempo tecnicamente necessario alla
                successiva cancellazione secondo quanto previsto nei presenti
                Termini e nella Privacy Policy.
              </li>
              <li>
                <span className="font-semibold text-neutral-900">
                  Natura e finalità.
                </span>{" "}
                Il trattamento consiste nelle operazioni tecniche necessarie a
                ricevere, memorizzare, organizzare, recuperare, visualizzare
                e utilizzare le informazioni secondo le istruzioni impartite
                dall’utente attraverso le funzionalità di Mastro.
              </li>
              <li>
                <span className="font-semibold text-neutral-900">
                  Categorie di interessati.
                </span>{" "}
                Possono essere coinvolti clienti dell’utente, potenziali
                clienti, collaboratori o altre persone i cui dati siano
                inseriti dall’utente nell’ambito di un lavoro.
              </li>
              <li>
                <span className="font-semibold text-neutral-900">
                  Categorie di dati.
                </span>{" "}
                Possono comprendere dati identificativi e di contatto,
                fotografie, informazioni relative a lavori e interventi, note,
                comunicazioni, indirizzi o luoghi di intervento e altre
                informazioni inserite dall’utente.
              </li>
            </UL>
            <P>Mastro si impegna, per tali trattamenti, a:</P>
            <UL>
              <li>
                trattare i dati esclusivamente secondo le istruzioni
                documentate dell’utente e per fornire il servizio;
              </li>
              <li>adottare misure tecniche e organizzative appropriate;</li>
              <li>
                garantire la riservatezza dei soggetti autorizzati ad accedere
                ai dati;
              </li>
              <li>
                assistere ragionevolmente l’utente nella gestione delle
                richieste degli interessati e degli obblighi applicabili;
              </li>
              <li>
                utilizzare eventuali sub-responsabili nel rispetto
                dell’articolo 28 GDPR;
              </li>
              <li>
                fornire le informazioni ragionevolmente necessarie a
                dimostrare il rispetto degli obblighi applicabili;
              </li>
              <li>
                al termine del rapporto, cancellare o restituire i dati
                secondo quanto applicabile, fatti salvi gli obblighi di
                conservazione previsti dalla legge.
              </li>
            </UL>
            <UL>
              <li>
                <span className="font-semibold text-neutral-900">
                  Sub-responsabili.
                </span>{" "}
                L’utente autorizza in via generale Mastro a utilizzare
                fornitori tecnici necessari all’erogazione del servizio,
                inclusi servizi di infrastruttura cloud, database,
                archiviazione, messaggistica, intelligenza artificiale e
                trascrizione. Mastro resta responsabile dell’utilizzo dei
                sub-responsabili nei limiti previsti dalla normativa
                applicabile. Le informazioni sui principali fornitori
                utilizzati possono essere richieste a{" "}
                <a
                  href="mailto:privacy@iltuomastroapp.it"
                  className="text-teal-700 underline-offset-4 hover:underline"
                >
                  privacy@iltuomastroapp.it
                </a>
                . Eventuali cambiamenti rilevanti nella catena dei fornitori
                che incidano sul trattamento per conto dell’utente saranno
                comunicati con modalità ragionevoli, consentendo all’utente
                di formulare eventuali osservazioni o, se non intende
                accettare il cambiamento, interrompere l’utilizzo del
                servizio.
              </li>
            </UL>

            <H2 n={12}>Utilizzi non consentiti</H2>
            <P>
              L’utente non deve utilizzare Mastro per attività illecite, per
              violare diritti di terzi, per tentare di accedere ai dati di
              altri utenti, compromettere il funzionamento o la sicurezza del
              servizio, distribuire malware o altri contenuti dannosi, oppure
              utilizzare il servizio in modo abusivo. L’utente dovrebbe
              inoltre evitare di inserire categorie particolarmente delicate
              di dati personali quando non siano realmente necessarie allo
              svolgimento del proprio lavoro.
            </P>

            <H2 n={13}>Sicurezza e separazione dei dati</H2>
            <P>
              Mastro adotta misure tecniche volte a proteggere gli account e
              mantenere separati i dati appartenenti a utenti diversi. Le
              password vengono memorizzate utilizzando tecniche di hashing e
              non in forma leggibile. Nessun sistema informatico può tuttavia
              garantire l’assenza assoluta di malfunzionamenti, vulnerabilità
              o incidenti. L’utente deve quindi utilizzare Mastro come
              strumento operativo e mantenere le cautele ragionevoli
              appropriate alla propria attività.
            </P>

            <H2 n={14}>Disponibilità della Beta</H2>
            <P>
              Mastro è un prodotto in evoluzione. Durante Pre-Beta e Beta
              possono verificarsi:
            </P>
            <UL>
              <li>malfunzionamenti;</li>
              <li>modifiche dell’interfaccia;</li>
              <li>interruzioni temporanee;</li>
              <li>variazioni delle funzionalità;</li>
              <li>interventi di manutenzione.</li>
            </UL>
            <P>
              Non viene garantito un livello di servizio o disponibilità
              continua 24 ore su 24. Questo non limita gli obblighi di Mastro
              relativi alla protezione e alla corretta gestione dei dati
              personali.
            </P>

            <H2 n={15}>Modifiche e sospensione del servizio</H2>
            <P>
              Durante la Beta Mastro può correggere errori, migliorare
              funzionalità o modificare parti del servizio. Non verranno
              tuttavia introdotti costi o abbonamenti a carico dell’utente
              senza una comunicazione preventiva e una nuova decisione
              esplicita dell’utente. L’accesso può essere sospeso quando
              ragionevolmente necessario per sicurezza, abuso del servizio,
              violazioni dei presenti Termini o esigenze tecniche urgenti.
            </P>

            <H2 n={16}>Chiusura dell’account</H2>
            <P>
              L’utente può interrompere l’utilizzo di Mastro in qualsiasi
              momento. Durante la Pre-Beta, la richiesta di cancellazione
              dell’account può essere inoltrata a{" "}
              <a
                href="mailto:privacy@iltuomastroapp.it"
                className="text-teal-700 underline-offset-4 hover:underline"
              >
                privacy@iltuomastroapp.it
              </a>
              . La richiesta viene gestita manualmente. I dati vengono
              eliminati dai sistemi attivi compatibilmente con i tempi
              tecnici necessari e con eventuali obblighi di legge. Eventuali
              copie di backup possono permanere temporaneamente fino alla
              normale rotazione dei backup. Archiviare o rimuovere dalla
              visualizzazione un singolo lavoro o preventivo non equivale
              alla cancellazione dell’account o dei relativi dati personali.
            </P>

            <H2 n={17}>Responsabilità dell’utente</H2>
            <P>
              L’utente è responsabile delle decisioni professionali prese
              sulla base delle informazioni conservate o prodotte tramite
              Mastro. In particolare deve verificare che i dati inseriti, i
              lavori, i preventivi e le informazioni economiche corrispondano
              alla realtà prima di utilizzarli verso i propri clienti. Mastro
              non deve essere utilizzato come unica fonte per decisioni che
              richiedano valutazioni professionali, fiscali, tecniche,
              legali o di sicurezza.
            </P>

            <H2 n={18}>Limiti di responsabilità</H2>
            <P>
              Mastro viene fornito durante la Beta con l’obiettivo di
              consentirne la sperimentazione in condizioni reali. Nei limiti
              consentiti dalla legge, il gestore non risponde di danni
              derivanti esclusivamente da informazioni errate inserite
              dall’utente, utilizzi del servizio contrari ai presenti Termini
              o indisponibilità di servizi di terze parti che non siano sotto
              il controllo di Mastro. Nulla nei presenti Termini esclude o
              limita responsabilità che non possano essere escluse o limitate
              ai sensi della legge applicabile.
            </P>

            <H2 n={19}>Proprietà di Mastro</H2>
            <P>
              Il software, il marchio Mastro, il design, l’interfaccia e gli
              altri elementi propri del prodotto restano nella disponibilità
              dei rispettivi titolari. L’utilizzo della Beta concede
              all’utente esclusivamente il diritto personale di utilizzare il
              servizio secondo i presenti Termini. Non è consentito
              utilizzare il marchio, copiare il servizio o cercare di
              appropriarsi del software o di altri elementi protetti, salvo
              quanto consentito dalla legge.
            </P>

            <H2 n={20}>Privacy</H2>
            <P>
              Il trattamento dei dati personali è descritto nella Privacy
              Policy di Mastro, disponibile separatamente. La Privacy Policy
              costituisce il riferimento per comprendere quali dati vengono
              trattati, per quali finalità, per quanto tempo, con quali
              fornitori e quali diritti possono essere esercitati.
            </P>

            <H2 n={21}>Modifiche ai Termini</H2>
            <P>
              Poiché Mastro è in fase Beta, i presenti Termini potranno
              essere aggiornati. In caso di modifiche rilevanti verrà
              aggiornata la data della versione e, quando necessario,
              l’utente sarà informato prima che le nuove condizioni producano
              effetti. L’introduzione futura di un servizio a pagamento
              richiederà condizioni specifiche separate e non sarà
              considerata una semplice modifica automatica della Beta
              gratuita.
            </P>

            <H2 n={22}>Legge applicabile</H2>
            <P>
              I presenti Termini sono regolati dalla legge italiana e dalla
              normativa dell’Unione europea applicabile. Per eventuali
              controversie si applicano i criteri di competenza stabiliti
              dalla legge. Non viene introdotta per la Beta una clausola di
              foro esclusivo ulteriore rispetto a quanto previsto dalla
              normativa applicabile.
            </P>

            <H2 n={23}>Contatti</H2>
            <P>Per informazioni sui presenti Termini:</P>
            <P>Riccardo Gerri</P>
            <P>Gestore di Mastro</P>
            <P>
              <a
                href="mailto:privacy@iltuomastroapp.it"
                className="text-teal-700 underline-offset-4 hover:underline"
              >
                privacy@iltuomastroapp.it
              </a>
            </P>
          </article>
        </LandingSection>
      </main>

      <LandingSection
        as="footer"
        className="border-t border-neutral-100"
        innerClassName="flex flex-col gap-2 py-6 text-xs text-neutral-400 sm:flex-row sm:items-center sm:justify-between"
      >
        <span>Mastro — la memoria operativa del professionista.</span>
        <div className="flex items-center gap-4">
          <Link
            to="/privacy"
            className="text-neutral-500 hover:text-neutral-700"
          >
            Privacy Policy
          </Link>
          <Link
            to="/termini"
            className="text-neutral-500 hover:text-neutral-700"
          >
            Termini
          </Link>
        </div>
      </LandingSection>
    </div>
  );
}
